import {BaseEntity, entity} from "@core/model";
import {translate} from "@util";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {useThemeContext} from "@theme";
import {Permission} from "@core/model";
import {SaveIcon, PermissionsIcon} from "@components/icons";
import {useEffect, useState} from "react";
import isEmpty from "lodash/isEmpty";
import {PermissionsTable} from "./PermissionsTable";
import {api} from "@core/api";
import {useDispatch} from "react-redux";

export type PermissionsDialogProps<T> = {
  open: boolean;
  data: Partial<T>;
  isLoading: boolean;
  entity: entity;
  onClose?: () => void;
};

function PermissionsDialog<T extends BaseEntity>(
  props: PermissionsDialogProps<T>,
) {
  const {open, data, isLoading, entity, onClose} = props;
  const dispatch = useDispatch();
  const {theme} = useThemeContext();

  const [update] = api.useUpdateEntityMutation();

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isValid, setIsValid] = useState<boolean>(false);

  useEffect(() => {
    setPermissions(data.permissions ?? []);
  }, [data.permissions]);

  useEffect(() => {
    const check = permissions.every(p => !isEmpty(p.operations) && p.value);
    setIsValid(check);
  }, [permissions]);

  const handleConfirm = () => {
    const payload = {
      id: data.id!,
      permissions: permissions,
    };
    update({payload, entity})
      .unwrap()
      .then(() => {
        dispatch(api.util.invalidateTags([{type: entity, id: data.id!}]));
      });
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <DialogContent>
        <Box
          sx={{
            bgcolor: theme.palette.secondary.dark,
            width: "64px",
            borderRadius: "32px",
            height: "64px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
          }}>
          <PermissionsIcon
            fontSize="large"
            sx={{
              color: theme.palette.getContrastText(
                theme.palette.secondary.dark,
              ),
            }}
          />
        </Box>
      </DialogContent>
      <DialogTitle align="center">
        {data.name
          ? `${translate("permissions.title")} ${translate("common.label.for")} ${data.name}`
          : translate("permissions.title")}
      </DialogTitle>
      <DialogContent>
        <PermissionsTable permissions={permissions} onChange={setPermissions} />
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => setPermissions(data.permissions ?? [])}>
          {translate("common.action.reset")}
        </Button>
        <Button
          startIcon={<SaveIcon />}
          variant="contained"
          disabled={!isValid}
          loading={isLoading}
          onClick={handleConfirm}>
          {translate("common.action.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export {PermissionsDialog};
